import {useQuery} from "@tanstack/react-query";
import {AxiosError} from "axios";
import {ProductService} from "../domain/product-service.entity";
import {ProductServiceSummary} from "../domain/product-service.types";
import {createProductServiceRepository} from "../data/repositories";
import {
  PaginatedResult,
  V2PaginatedResult,
} from "../data/mappers/product-service.mapper.types";

/**
 * Query keys for product service cache management.
 */
export const PRODUCT_SERVICE_QUERY_KEYS = {
  all: ["PRODUCT_SERVICE"] as const,
  single: (id: number) => ["PRODUCT_SERVICE", String(id)] as const,
  list: (page: number, perPage: number) =>
    ["PRODUCT_SERVICE", "list", page, perPage] as const,
};

/**
 * Hook for fetching a single product service by ID.
 *
 * @param id - The product service ID to fetch
 * @param enabled - Whether the query should run (default: true if id provided)
 * @returns React Query result with ProductService entity
 *
 * @example
 * const { data: productService, isLoading, error } = useProductService(123);
 */
export const useProductService = (id?: number, enabled: boolean = true) => {
  const repository = createProductServiceRepository();

  return useQuery<ProductService, AxiosError<{status: boolean; message: string}>>({
    queryKey: PRODUCT_SERVICE_QUERY_KEYS.single(id ?? 0),
    queryFn: () => repository.getSingle(id!),
    enabled: enabled && !!id && id > 0,
  });
};

/**
 * Hook for fetching paginated list of product services.
 *
 * @param page - Page number (1-indexed)
 * @param perPage - Number of items per page
 * @returns React Query result with paginated ProductService entities
 *
 * @example
 * const { data, isLoading } = useProductServices(1, 10);
 * const summaries = data?.data; // ProductServiceSummary[]
 * const hasMore = 'hasNextPage' in data ? data.hasNextPage : data.meta.currentPage < data.meta.lastPage;
 */
export const useProductServices = (page: number = 1, perPage: number = 5) => {
  const repository = createProductServiceRepository();

  return useQuery<
    PaginatedResult<ProductServiceSummary> | V2PaginatedResult<ProductServiceSummary>,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: PRODUCT_SERVICE_QUERY_KEYS.list(page, perPage),
    queryFn: () => repository.getList({page, perPage}),
    staleTime: Infinity,
    refetchOnWindowFocus: true,
    keepPreviousData: true,
  });
};
